import { type MouseEvent, type PointerEvent, useRef, useState } from 'react';

const SWIPE_THRESHOLD_PX = 48;
// How far a press travels before it is a drag. A press on a control gets a
// wider margin, or a tap that wobbles would lose its click.
const DRAG_SLOP_PX = 4;
const CONTROL_DRAG_SLOP_PX = 10;
// The click a browser may still send after a drag is released.
const CLICK_AFTER_DRAG_MS = 500;

export type SwipeDirection = 'left' | 'right' | 'both' | 'down';

interface SwipeOptions {
   /**
    * Let a drag start on a button or a link too, as a sheet's does: a tap
    * keeps its click, and a press that turns into a drag loses it. A menu
    * trigger opens on press, so it never starts one.
    */
   fromControls?: boolean;
}

/**
 * A drag toward the element's own edge dismisses it past the threshold and
 * snaps back short of it: sideways (either way from a centered column), or
 * down for a bar at the bottom of the screen. The other axis is left to
 * the page, through the element's `touch-action`. Unless `fromControls`,
 * a drag that starts on a button or a link is theirs.
 *
 * The offset is stamped with the revision it belongs to, so an element
 * shown again after a swipe dismissed it comes back where it started, not
 * where the swipe left it.
 */
export function useSwipe(
   direction: SwipeDirection,
   revision: number,
   onSwipeOut: () => void,
   { fromControls = false }: SwipeOptions = {}
) {
   const [swipe, setSwipe] = useState({ revision, offsetPx: 0, isSwiping: false });
   const { offsetPx, isSwiping } =
      swipe.revision === revision ? swipe : { offsetPx: 0, isSwiping: false };
   const startRef = useRef<{ x: number; y: number; slopPx: number } | null>(null);
   const draggedRef = useRef(false);
   const swallowClickUntilRef = useRef(0);

   const offsetFrom = (start: { x: number; y: number }, event: PointerEvent<HTMLElement>) => {
      if (direction === 'down') return Math.max(0, event.clientY - start.y);
      const dx = event.clientX - start.x;
      if (direction === 'right') return Math.max(0, dx);
      if (direction === 'left') return Math.min(0, dx);
      return dx;
   };

   const handlers = {
      onPointerDown: (event: PointerEvent<HTMLElement>) => {
         // A new press is a new gesture: a drag whose click never came, or
         // landed off the element, must not eat this one's.
         swallowClickUntilRef.current = 0;
         // React bubbles a portal's events through the tree, so a menu or a
         // dialog rendered inside the element would drag it too.
         if (event.button !== 0 || !event.currentTarget.contains(event.target as Node)) return;
         const control = (event.target as Element).closest('button, a');
         if (control && (!fromControls || control.matches('[aria-haspopup]'))) return;
         startRef.current = {
            x: event.clientX,
            y: event.clientY,
            slopPx: control ? CONTROL_DRAG_SLOP_PX : DRAG_SLOP_PX,
         };
         draggedRef.current = false;
      },
      onPointerMove: (event: PointerEvent<HTMLElement>) => {
         const start = startRef.current;
         if (!start) return;
         const offset = offsetFrom(start, event);
         if (!draggedRef.current) {
            if (Math.abs(offset) < start.slopPx) return;
            draggedRef.current = true;
            // Captured only once it is a drag, so a tap still lands on its control.
            event.currentTarget.setPointerCapture?.(event.pointerId);
         }
         setSwipe({ revision, offsetPx: offset, isSwiping: true });
      },
      onPointerUp: (event: PointerEvent<HTMLElement>) => {
         const start = startRef.current;
         startRef.current = null;
         if (!start || !draggedRef.current) return;
         draggedRef.current = false;
         swallowClickUntilRef.current = Date.now() + CLICK_AFTER_DRAG_MS;
         const offset = offsetFrom(start, event);
         if (Math.abs(offset) >= SWIPE_THRESHOLD_PX) {
            setSwipe({ revision, offsetPx: offset, isSwiping: false });
            onSwipeOut();
         } else {
            setSwipe({ revision, offsetPx: 0, isSwiping: false });
         }
      },
      onPointerCancel: () => {
         startRef.current = null;
         draggedRef.current = false;
         setSwipe({ revision, offsetPx: 0, isSwiping: false });
      },
      // A drag that ends over a control is not a tap on it.
      onClickCapture: (event: MouseEvent<HTMLElement>) => {
         if (Date.now() > swallowClickUntilRef.current) return;
         if (!event.currentTarget.contains(event.target as Node)) return;
         swallowClickUntilRef.current = 0;
         event.preventDefault();
         event.stopPropagation();
      },
   };

   return { handlers: { ...handlers, 'data-swiping': isSwiping ? 'true' : 'false' }, offsetPx };
}
